import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { cleanText } from '../utils/textUtils';
import LocationDisplay from '../components/LocationDisplay';
import FavoriteButton from '../components/FavoriteButton';
import ShareButton from '../components/ShareButton';
import './ClassesPage.css';
import './SharedCardStyles.css';

interface Class { id: string; slug: string; name: string; short_description: string; class_type: string; category: string; skill_level: string; venue_name: string; address?: string; city: string; latitude?: number; longitude?: number; schedule_type: string; days_of_week: string[]; time_slots?: string; duration_minutes: number; enrollment_status: string; season: string; price_per_month: number; price_description?: string; trial_available: boolean; trial_price?: number; age_min: number; age_max: number; provider_name: string; class_size_max?: number; registration_url?: string; }

const ClassesPage: React.FC = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string>('all');

  useEffect(() => { fetchClasses(); }, []);

  const fetchClasses = async () => {
    try {
      setLoading(true); setError(null);
      let query = supabase.from('classes').select('*').eq('status', 'active');
      const selectedCity = localStorage.getItem('selectedCity');
      if (selectedCity) query = query.eq('city', selectedCity);
      const { data, error } = await query.order('class_type', { ascending: true });
      if (error) throw error;
      setClasses(data || []);
    } catch (err: any) { setError(err.message || 'Failed to load classes'); } finally { setLoading(false); }
  };

  const filteredClasses = (selectedType === 'all' ? classes : classes.filter(c => c.class_type === selectedType))
    .filter(c => c.id && c.id !== 'null' && c.id !== 'undefined');
  const classTypes = Array.from(new Set(classes.map(c => c.class_type)));
  const formatDays = (days: string[]) => !days || days.length === 0 ? '' : days.join(', ');
  const handleWebsite = (url: string | undefined) => { if (url) window.open(url, '_blank', 'noopener,noreferrer'); };

  if (loading) return <div className="classes-container"><div className="loading"><div className="spinner"></div><p>Loading classes...</p></div></div>;
  if (error) return <div className="classes-container"><div className="error"><p>😕 Oops! {error}</p><button onClick={fetchClasses} className="retry-btn">Try Again</button></div></div>;

  return (
    <div className="classes-container">
      <button className="back-button" onClick={() => navigate('/')}>← Back to Home</button>
      <LocationDisplay />
      <div className="classes-header"><h1>🎓 Classes</h1><p>Year-round enrollment • Music • Sports • Martial Arts</p></div>
      <div className="filter-bar">
        <button className={`filter-btn ${selectedType === 'all' ? 'active' : ''}`} onClick={() => setSelectedType('all')}>All Classes</button>
        {classTypes.map(type => <button key={type} className={`filter-btn ${selectedType === type ? 'active' : ''}`} onClick={() => setSelectedType(type)}>{type}</button>)}
      </div>
      {filteredClasses.length === 0 ? <div className="no-classes"><p>🎓 No classes found. Check back soon!</p></div> : (
        <div className="classes-list">
          {filteredClasses.map(classItem => (
            <div key={classItem.id} className="class-card">
              <div className="favorite-button-wrapper"><FavoriteButton itemId={classItem.id} itemType="class" size="small" /></div>
              <div className="class-header-badge"><span className="class-category">{classItem.category}</span><span className="class-type">{classItem.class_type}</span></div>
              <div className="class-content">
                <h3 className="class-name">{classItem.name}</h3>
                <p className="class-venue-name">{classItem.venue_name}</p>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    classItem.address 
                      ? `${classItem.venue_name} ${classItem.address}, ${classItem.city}`.trim()
                      : `${classItem.venue_name}, ${classItem.city}`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="location-link class-address"
                  onClick={(e) => e.stopPropagation()}
                >
                  📍 {classItem.address || classItem.city}
                </a>
                <p className="class-description">{cleanText(classItem.short_description)}</p>
                <div className="class-details">
                  <div className="detail"><span className="detail-icon">📅</span><span>{classItem.schedule_type} • {formatDays(classItem.days_of_week)}</span></div>
                  <div className="detail"><span className="detail-icon">⏰</span><span>{classItem.duration_minutes} min sessions</span></div>
                  <div className="detail"><span className="detail-icon">👶</span><span>Ages {classItem.age_min}-{classItem.age_max}</span></div>
                  <div className="detail"><span className="detail-icon">💰</span><span>${classItem.price_per_month}/month</span></div>
                  <div className="detail"><span className="detail-icon">🎯</span><span>{classItem.skill_level}</span></div>
                </div>
                <div className="class-features">
                  {classItem.trial_available && <span className="feature-badge">✨ Try First Class ${classItem.trial_price}</span>}
                  {classItem.enrollment_status === 'open' && <span className="feature-badge enrollment-open">✅ Enrolling Now</span>}
                  {classItem.enrollment_status === 'waitlist' && <span className="feature-badge enrollment-waitlist">⏳ Waitlist Available</span>}
                </div>
                <div className="class-actions">
                  <button 
                    className={`action-button icon-only ${!classItem.registration_url ? 'disabled' : ''}`}
                    onClick={() => handleWebsite(classItem.registration_url)}
                    disabled={!classItem.registration_url}
                    title="Visit Website"
                    aria-label="Visit Website"
                  >
                    🌐
                  </button>
                  <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                    <ShareButton 
                      title={classItem.name}
                      description={classItem.short_description}
                      url={`${window.location.origin}/classes`}
                      location={`${classItem.venue_name}, ${classItem.city}`}
                      size="small"
                      showLabel={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClassesPage;
